import {
  CBookingStatusDes,
  CExamStatusDes,
  C_BOOKING_FILTERS,
  C_EXAM_FILTERS
} from '../constants'
import { EStatusBookingType } from '../enums'
import { IBooking } from '../interfaces'
import { getColumnWidth } from '../logic/getColumnWidth'
import useColumnBooking from '../logic/useColumnBooking'
import HeaderBooking from './headerBooking'
import PanelBooking from './panelBooking'
import { Button, Col, Radio, RadioChangeEvent, Row } from 'antd'
import { FooterTable } from 'components/atoms'
import { ActionType } from 'components/atoms/actions'
import { NO_DATA } from 'constants/common'
import {
  SEARCH_QUERY_BOOKING_DEFAULT,
  SEARCH_QUERY_BOOKING_EXAM_STATUS_DEFAULT,
  SEARCH_QUERY_URL
} from 'constants/query'
import { useOnlineStatus } from 'detectConnectServer'
import { useModalConfirm, useQuerySearch } from 'hooks'
import { IConfigModalConfirm } from 'interfaces'
import moment from 'moment'
import useModalReceive from 'pages/patientReceive/logic/useModalReceive'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useBookingServices } from 'services'
import useProcessBookingService from 'services/useProcessBookingService'
import { ESocketEventName, socketIoService } from 'socket'
import { StyledTable } from 'styles/styledGlobal'
import { formatDate, getOldPatient, getTimeToday } from 'utils'
import { exportToExcel } from 'utils/exportExcel'

const BookingContainer = () => {
  const [statusBookingType, setStatusBookingType] =
    useState<EStatusBookingType>(EStatusBookingType.BookingStatus)
  const tabButtonsFilter =
    statusBookingType === EStatusBookingType.BookingStatus
      ? C_BOOKING_FILTERS
      : C_EXAM_FILTERS

  const filterDefault = tabButtonsFilter[0]
  const {
    items,
    setItems,
    handleOnSearch,
    totalRecord,
    loading,
    payloadSearch,
    reload,
    offset,
    getPayloadSearch,
    getItems
  } = useQuerySearch<IBooking>(SEARCH_QUERY_URL.Booking, {
    ...SEARCH_QUERY_BOOKING_DEFAULT,
    query: {
      ...filterDefault.value
    }
  })

  const { t } = useTranslation()
  const { columnsBooking } = useColumnBooking(offset, totalRecord, handleAction)
  const { cancelBooking, searchBooking } = useBookingServices()
  const { updateExamStatusWaiting } = useProcessBookingService()
  const { onConfirmDelete, onConfirmAccept } = useModalConfirm('confirm-cancel')
  const { handleEditBooking } = useModalReceive({
    ignoreNavigate: true
  })
  const isOnline = useOnlineStatus()

  const queryDefault =
    statusBookingType === EStatusBookingType.BookingStatus
      ? SEARCH_QUERY_BOOKING_DEFAULT
      : SEARCH_QUERY_BOOKING_EXAM_STATUS_DEFAULT
  useEffect(() => {
    let query = { ...queryDefault.query, ...getTimeToday() }
    if (payloadSearch) {
      if (statusBookingType === EStatusBookingType.BookingStatus) {
        query = {
          ...payloadSearch.query,
          ...queryDefault.query
        }
        if (query?.exam_status) delete query.exam_status
      } else {
        query = {
          ...payloadSearch.query,
          status: queryDefault.query?.status,
          exam_status: queryDefault.query?.exam_status
        }
      }
    }
  }, [statusBookingType])
  useEffect(() => {
    console.log('[BookingContainer] isOnline: ', isOnline)
    const query = { ...queryDefault.query, ...getTimeToday() }
    handleOnSearch({
      ...queryDefault,
      limit: payloadSearch?.limit || 50,
      query
    })
  }, [isOnline])

  function handleAction(action: ActionType, booking: IBooking) {
    switch (action) {
      case ActionType.Edit:
        handleEditBooking(booking, (res) => {
          if (res._successfully) reload()
        })
        break
      case ActionType.Delete:
        handleCancelBooking(booking)
        break
      case ActionType.Accept:
        handleOnAcceptBooking(booking)
        break

      default:
        break
    }
  }

  const handleOnAcceptBooking = (booking: IBooking) => {
    const configModal: IConfigModalConfirm = {
      message: t('receive-patient'),
      title: 'confirm-receive-success',
      description: 'cannot-undo',
      icon: '/images/ic-accept.svg'
    }
    const onAcceptBooking = async () => {
      const res = await updateExamStatusWaiting(booking?.booking_id)
      reload()
      return res
    }
    onConfirmAccept(configModal, onAcceptBooking)
  }

  const handleCancelBooking = (booking: IBooking) => {
    const messageSuccess = `${t('booking-cancel', {
      patient: `"${booking?.patient?.full_name}"`
    })}`
    const onCancelBooking = async (reason: string) => {
      const query = {
        booking_id: booking?.booking_id || 0,
        cancel_reason: reason
      }
      const res = await cancelBooking(query)
      reload()
      return res
    }
    onConfirmDelete(
      {
        message: messageSuccess,
        okLabel: 'cancel-booking',
        description: 'cannot-undo',
        title: t('are-you-sure-cancel-booking', {
          patient: booking?.patient?.full_name
        })
      },
      onCancelBooking
    )
  }

  const handleExportExcel = async () => {
    const res = await searchBooking({
      ...payloadSearch,
      offset: undefined,
      limit: 10000000
    })
    if (res) {
      const dataExcel = res.data.bookings.map((item: IBooking, index) => {
        return {
          hash: index + 1,
          booking_no: item?.booking_no || NO_DATA,
          time: `${formatDate(item?.from, 'HH:mm')} - ${formatDate(
            item?.to,
            'HH:mm DD/MM/YYYY'
          )}`,
          booking_code: item?.booking_code || NO_DATA,
          user_code: item?.patient?.user_code || NO_DATA,
          phone_number: item?.patient?.phone_number || NO_DATA,
          full_name: item?.patient?.full_name || NO_DATA,
          gender: t(`gender-${item?.patient?.gender}`),
          date_of_birth: getOldPatient(item?.patient?.year) || NO_DATA,
          name:
            item?.metadata?.initial_services?.services
              ?.map((service: any) => service?.name)
              ?.join(', ') || NO_DATA,
          major_name: Array.from(
            new Set(
              item?.metadata?.initial_services?.services?.map(
                (service: any) => service?.major_name
              )
            )
          )?.join(', '),
          status: t(CBookingStatusDes[item?.status]),
          exam_status: t(CExamStatusDes[item?.exam_status])
        }
      })

      const headerNames = [
        t('hash'),
        t('numeric-examination-schedule'),
        t('time'),
        t('schedule-code'),
        t('patient-code'),
        t('phone-number'),
        t('fullname'),
        t('gender'),
        t('old'),
        t('service'),
        t('major'),
        t('booking-status'),
        t('examination-status')
      ]
      const columnWidths = getColumnWidth(dataExcel, headerNames)
      exportToExcel(
        `DS Lich Hen - ${moment(new Date()).format('DD.MM.YYYY')}`,
        dataExcel,
        {
          headerNames: headerNames,
          columnWidths: columnWidths
        }
      )
    }
  }

  function checkCurFilter(curFilter: any, data: any): boolean {
    const fromTimeDataInput = data['from_time'] || data['from']
    const toTimeDataInput = data['to_time'] || data['to']
    if (curFilter?.query) {
      if (
        curFilter.query['from'] &&
        curFilter.query['to'] &&
        fromTimeDataInput &&
        toTimeDataInput
      ) {
        const from = curFilter.query['from']
        const to = curFilter.query['to']
        const fromTimeData = new Date(fromTimeDataInput).getTime()
        const toTimeData = new Date(toTimeDataInput).getTime()
        if (
          // isNaN(fromTimeData) ||
          // isNaN(toTimeData) ||
          !(from <= fromTimeData && toTimeData <= to)
        ) {
          return false
        }
      }
    }
    return true
  }
  function updateDataBooking(
    originalData: IBooking,
    updateData: any
  ): IBooking {
    originalData['from'] = updateData['from_time']
    originalData['to'] = updateData['to_time']
    return originalData
  }

  useEffect(() => {
    const handleCreateBooking = (data: any) => {
      const curFilter = getPayloadSearch()
      if (curFilter.offset === 0 && checkCurFilter(curFilter, data)) {
        reload(true)
      }
    }

    const handleUpdateBooking = (data: any) => {
      const curFilter = getPayloadSearch()
      const items = getItems()
      const existingIndex = items?.findIndex(
        (item: IBooking) => item.booking_id === data.booking_id
      )

      if (existingIndex > -1) {
        if (checkCurFilter(curFilter, data)) {
          setItems((prevItems) => {
            const updatedItems = [...prevItems]
            updatedItems[existingIndex] = updateDataBooking(
              updatedItems[existingIndex],
              data
            )
            return updatedItems
          })
        } else if (curFilter.offset === 0) {
          reload(true)
        }
      } else if (curFilter.offset === 0) {
        reload(true)
      }
    }

    socketIoService.subscribeCreateBooking(handleCreateBooking)
    socketIoService.subscribeUpdateBooking(handleUpdateBooking)

    return () => {
      socketIoService.unsubscribeEvent(ESocketEventName.MANAGE_BOOKING_TOPIC)
      socketIoService.unsubscribeEvent(
        ESocketEventName.MANAGE_BOOKING_UPDATE_TOPIC
      )
    }
  }, [setItems])

  return (
    <>
      <Row gutter={16}>
        <Col className="gutter-row" span={12}>
          <Radio.Group
            onChange={(e: RadioChangeEvent) =>
              setStatusBookingType(e.target.value)
            }
            value={statusBookingType}
            style={{ marginBottom: 8 }}>
            <Radio.Button value={EStatusBookingType.BookingStatus}>
              {t('booking-status')}
            </Radio.Button>
            <Radio.Button value={EStatusBookingType.ExamStatus}>
              {t('exam-status')}
            </Radio.Button>
          </Radio.Group>
        </Col>
        <Col className="gutter-row" span={12} style={{ textAlign: 'right' }}>
          <Button type="primary" onClick={handleExportExcel}>
            {t('export-excel')}
          </Button>
        </Col>
      </Row>
      <HeaderBooking
        onSearch={(query) =>
          handleOnSearch({
            ...SEARCH_QUERY_BOOKING_DEFAULT,
            query: { ...payloadSearch?.query, ...query },
            limit: payloadSearch?.limit,
            offset: 0
          })
        }
        payloadSearch={payloadSearch}
      />
      <PanelBooking
        onChange={(item: any) => {
          handleOnSearch({
            ...SEARCH_QUERY_BOOKING_DEFAULT,
            query: { ...payloadSearch?.query, ...item.value },
            limit: payloadSearch?.limit
          })
        }}
        totalRecord={loading ? undefined : totalRecord}
        tabsButton={tabButtonsFilter}
        tabDefault={filterDefault}
      />
      <StyledTable
        sticky
        columns={columnsBooking}
        dataSource={items}
        pagination={false}
        rowKey="booking_id"
        loading={loading}
        $maxHeight="calc(100vh - 512px)"
        footer={() => (
          <FooterTable
            forPage={SEARCH_QUERY_URL.Booking.name}
            totalRecord={totalRecord}
            showOptionAll
          />
        )}
      />
    </>
  )
}

export default BookingContainer
